import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/el";
import "dayjs/locale/en";

import { useAttended } from "./useAttended";
import { TAttendedClassEvent } from "../../../models/ClassEvent";
import { OpenGymLabel } from "../../../components/OpenGymLabel/OpenGymLabel";
import starIcon from "../../../assets/images/star.svg";

import "./Attended.scss";
import LeaveARatingModal from "../../../components/LeaveARatingModal/LeaveARatingModal";

dayjs.extend(utc);
dayjs.extend(localizedFormat);

const Attended: React.FC = () => {
  const { t } = useTranslation();
  const {
    selectedEvent,
    isLoading,
    attendedClassEvents,
    onClickGymImage,
    onLeaveAReview,
    onCloseLeaveAReview,
    onSuccessLeaveARating,
  } = useAttended();

  return (
    <div className="attended">
      <h1 className="attended__title">
        {t("Attended")} ({attendedClassEvents.length || 0})
      </h1>

      {isLoading ? (
        <Spinner />
      ) : attendedClassEvents?.length ? (
        <ul className="attended__list">
          {attendedClassEvents.map((event, i) => (
            <React.Fragment key={event.id}>
              <AttendedItem
                event={event}
                onLeaveAReview={onLeaveAReview}
                onClickGymImage={onClickGymImage}
              />
              {i !== attendedClassEvents.length - 1 && <span className="attended__divider" />}
            </React.Fragment>
          ))}
        </ul>
      ) : (
        <p className="attended__empty">{t("NoAttendedClassesYet")}</p>
      )}

      <LeaveARatingModal
        open={!!selectedEvent}
        onClose={onCloseLeaveAReview}
        onSuccessLeaveARating={onSuccessLeaveARating}
        event={selectedEvent}
      />
    </div>
  );
};

interface AttendedItemProps {
  event: TAttendedClassEvent;
  onLeaveAReview: (classEvent: TAttendedClassEvent) => void;
  onClickGymImage: (gymId: string) => void;
}

const AttendedItem: React.FC<AttendedItemProps> = ({ event, onLeaveAReview, onClickGymImage }) => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language === "gr" ? "el" : i18n.language || "en";
  const isOpenGym = event.type !== "GROUP_CLASS";

  const start = useMemo(
    () =>
      dayjs
        .utc(isOpenGym ? event.startDate : event.occuranceDate)
        .local()
        .locale(locale),
    [event, locale, isOpenGym]
  );

  const end = useMemo(
    () => (!isOpenGym ? start.add(event.duration, "minute") : null),
    [start, event, isOpenGym]
  );

  const date = start.format("dddd, D MMM");
  const time = isOpenGym
    ? start.format("h:mm A")
    : `${start.format("h:mm A")} - ${end?.format("h:mm A")}`;

  return (
    <li className="attended__item">
      {event.gymListingImage && (
        <img
          onClick={() => onClickGymImage(event.gymListingId as string)}
          className="attended__item-image"
          src={event.gymListingImage as string}
          alt={event.gymName}
        />
      )}

      <div className="attended__item-content">
        <div>
          <div className="attended__item-header">
            <h4 className="attended__item-title">{event.gymName}</h4>
            {isOpenGym && <OpenGymLabel />}
          </div>

          <div className="attended__item-info">
            <p className="attended__item-text">
              {date} {" | "} {time}
            </p>
            <p className="attended__item-text">{event.address}</p>
            <p className="attended__item-text">
              {event.creditsValue} {t("Credits")}
            </p>
          </div>
        </div>

        {/* {event.canLeaveComment ? ( */}
        <div className="attended__item-review" onClick={() => onLeaveAReview(event)}>
          <img className="attended__item-review-icon" src={starIcon} alt="Leave a review" />
          <p className="attended__item-review-text">{t("LeaveAReview")}</p>
        </div>
        {/* ) : (
          <span />
        )} */}
      </div>
    </li>
  );
};

const Spinner: React.FC = () => (
  <div className="attended__spinner">
    <Spin size="large" />
  </div>
);

export default Attended;
